import React, { useMemo, useContext, useCallback, useRef, useEffect, useState } from 'react'
import { treeHandlers } from 'react-hyper-tree'
import pt from 'prop-types'
import noop from 'lodash/noop'
import { ThemeContext } from 'styled-components'
import PortalTooltip from '@/components/blocks/PortalTooltip'
import Loader from '@/components/blocks/Loader'
import viewTreeIcons from '@/constants/viewTree'
import ArrowIcon from '@/components/icons/maps/ArrowIcon'
import { STATUS_COLORS } from '@/constants/styles/mapsMarks'
import {
  TreeNodeArrow,
  TreeNode,
  TreeNodeIcon,
  TreeNodeTitle,
  TreeNodeObjects,
  TreeNodeInfo,
  TreeNodeIconAndInfo,
  TreeNodeToolTip,
  ButtonGetMore,
  ConteinerButtonGetMore,
} from './styles'
import {
  ERROR,
  INFO,
  INSTALLED,
  NEW,
  NOT_EXIST,
  NOT_INSTALLED,
  NOT_VERIFIED,
  OFF,
  OK,
  UNINSTALLED,
  VERIFIED,
  WARNING
} from '@/constants/objectStatuses'
import { GROUP, INTEGRATION, OBJECT_ELEMENT } from '@/constants/objectTypes'
import { FormattedMessage as Lang, injectIntl } from 'react-intl'
import CheckBox from '@/components/controls/CheckBox'

const deleteNodeChildren = (node = {}, defaultParameters = {}) => ({
  ...node,
  children: null,
  data: {
    ...(node.data || {}),
    children: null,
  },
  options: {
    ...(node.options || {}),
    parent: null,
  },
  ...defaultParameters,
})

export const STATE_TO_STATUS = {
  OK,
  WARNING,
  OFF,
  NO_PROBLEM: 'NO_PROBLEM',
  ERROR: 'ERROR',
  NOT_VERIFIED,
  VERIFIED,
  NEW,
  NOT_EXIST,
  NOT_INSTALLED,
  INSTALLED,
  UNINSTALLED,
  UNDEFINED: INFO,
  undefined: undefined,
  null: INFO,
  NO_CONNECTION: ERROR,
}

const TreeNodeContainer = ({
  node,
  onToggle,
  onSelect,
  onSelectNode,
  isMonitoring,
  selectedNode,
  isSchedule,
  isSearchMode,
  id,
  intl,
  checkTree,
  onCheckNode,
  countRef,
}) => {
  const ref = useRef(null)
  const isSelectedNode = node.id === selectedNode?.id

  const [localLoading, setLocalLoading] = useState(false)

  useEffect(() => {
    if (isSelectedNode && ref.current) {
      if (typeof ref.current.scrollIntoViewIfNeeded === "function") {
        ref.current.scrollIntoViewIfNeeded();
      } else {
        ref.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest"
        });
      }
    }
  }, [ref, isSelectedNode]);

  useEffect(()=> {
    if (checkTree && node.data.checked) {
      onCheckNode([node.data])
    }
  },[checkTree, node])



  const hendleGetMore = () => {
    setLocalLoading(true)

    if (!countRef.current[node.data.parentId]) {
      countRef.current = {
        ...countRef.current,
        [node.data.parentId]: 2
      }
    } else {
      countRef.current = {
        ...countRef.current,
        [node.data.parentId]: countRef.current[node.data.parentId] + 1
      }
    }

    const tree = treeHandlers.trees[id]
    const parent = tree.instance.getNodeById(node.data.parentId)
    parent.data.getChildren(parent, countRef.current[node.data.parentId])
      .then((parentChildren) => tree.handlers.setRawChildren(parent, parentChildren, 'first', true)).then(() => setLocalLoading(false))

  }



  const Icon = viewTreeIcons[node.data.type] || viewTreeIcons[node.data.treeNodeType]
  const theme = useContext(ThemeContext)
  const nodeStatus = useMemo(() => {
    return node.data.treeNodeType === OBJECT_ELEMENT ? STATE_TO_STATUS[isMonitoring ? node.data.telemetryState : node.data.state] : ''
  }, [node, isMonitoring])

  const nodeForSave = useMemo(() => ({
    ...node.data,
    original: deleteNodeChildren(node, {
      options: {
        ...node.options,
        parent: deleteNodeChildren(node.options.parent),
      },
    }),
  }), [node])

  const handleSelect = useCallback((event) => {
    event.stopPropagation()
    onSelect(event)
    onSelectNode(nodeForSave, 'click')
  }, [onSelect, onSelectNode, nodeForSave])

  const handleCheck = useCallback(() => {
    node.data.checked = !node.data.checked
    const tree = treeHandlers.trees['objects-groups-form-tree']
    tree.handlers.setNodeData(node.data.id, node.data)
    onCheckNode([node.data])
  }, [node, onCheckNode])

  const checkChildren = (childrenNodes, checkValue) => {
    const tree = treeHandlers.trees['objects-groups-form-tree']
    childrenNodes.forEach(item => {
      if (item.children.length) {
        checkChildren(item.children, checkValue)
      }
    })

    const children = childrenNodes.map(child => {
      return {
        ...child.data,
        checked: checkValue,
        childrenChecked: checkValue,
      }
    })
    children.forEach(item => {
      tree.handlers.setNodeData(item.id, item)
    })
    onCheckNode(children)
  }

  const handleCheckChildren = useCallback(() => {
    node.data.childrenChecked = !node.data.childrenChecked
    checkChildren(node.children, node.data.childrenChecked)
    const tree = treeHandlers.trees['objects-groups-form-tree']
    tree.handlers.setNodeData(node.data.id, node.data)
  }, [node, onCheckNode])

  const handleToggle = useCallback((event) => {
    event.stopPropagation()
    onToggle(event)
    // if (checkTree) {
    //   const tree = treeHandlers.trees['objects-groups-form-tree']
    //   console.log(node)
    //   const formattedNode = {
    //     ...nodeForSave,
    //     childrenChecked: !!node.children.length ? node.children.every(child => child.data.checked) : node.data.childrenChecked,
    //   }
    //   console.log(formattedNode)
    //   tree.handlers.setNodeData(node.data.id, formattedNode)
    //   onSelectNode(formattedNode, 'toggle')
    //   return
    // }
    onSelectNode(nodeForSave, 'toggle')
  }, [onToggle, onSelectNode, nodeForSave])

  const childrenCount = node.data.childrenCount

  const returnName = ({name, type, nodeType}) => {
    // const limit = 20
    const treeNodeWidth = document.getElementById('treeNode')?.clientWidth
    const treeNodeInfoWidth = document.getElementById('treeNodeInfo')?.clientWidth

    return (
      <>
        <PortalTooltip
              title={name}
              renderChildren={(wrapperRef, onMouseEnterHandler, onMouseLeaveHandler) => (
                <TreeNodeTitle
                  ref={wrapperRef}
                  onMouseEnter={onMouseEnterHandler}
                  onMouseLeave={onMouseLeaveHandler}
                  width={treeNodeWidth-treeNodeInfoWidth}
                  isSearchMode={isSearchMode}
                  type={type === INTEGRATION || type === OBJECT_ELEMENT || nodeType === INTEGRATION || nodeType === OBJECT_ELEMENT}
                  groupsTree={id.includes('groups')}
                >
                  {name}
                </TreeNodeTitle>
              )}
            />
        {/* {((name || '').length > limit)
          ? (
            <PortalTooltip
              title={name}
              renderChildren={(wrapperRef, onMouseEnterHandler, onMouseLeaveHandler) => (
                <TreeNodeTitle
                  ref={wrapperRef}
                  onMouseEnter={onMouseEnterHandler}
                  onMouseLeave={onMouseLeaveHandler}
                  width={treeNodeWidth-treeNodeInfoWidth}
                  type={type === INTEGRATION || type === OBJECT_ELEMENT}
                >
                  {name}
                </TreeNodeTitle>
              )}
            />
          )
          : (
            <TreeNodeTitle>
              {name}
            </TreeNodeTitle>
          )} */}
      </>
    )
  }

  const renderChildrenCount = () => {
    if (id === 'PPR-tree') {
      return null
    }

    return !!childrenCount && `${isSchedule ? intl.messages['globalNames.tree.schedules'] : intl.messages['globalNames.tree.objects']}: ${ childrenCount || intl.messages['globalNames.tree.unknown']}`
  }

  const hasChildrenTest = () => {
    if (id === 'PPR-tree' && node.data.treeNodeType === 'INTEGRATION') {
      return false
    }

    if (id === 'inventory-tree' && node.data.treeNodeType === 'PROJECT') {
      return false
    }

    return node.children.length > 0 || node.data.childrenCount > 0
  }

  return (
    <TreeNode isRoot={node.options.root} ref={isSelectedNode ? ref : null} id='treeNode' isLast={node.data.isLast}>
      <TreeNodeIconAndInfo onClick={handleSelect} id='treeNodeInfo'>
        {checkTree && !node.data.isSupportObject && node.data.nodeType === OBJECT_ELEMENT && node.data.installationType !== GROUP &&
          <CheckBox
            id="nodeCheck"
            value={node.data.checked}
            onChange={handleCheck}
          />
        }
        {Icon &&
          <PortalTooltip
            title={<Lang id={`globalNames.objectTypes.${ node.data.type || node.data.treeNodeType }`} />}
            renderChildren={(wrapperRef, onMouseEnterHandler, onMouseLeaveHandler) => (
              <TreeNodeToolTip
                ref={wrapperRef}
                onMouseEnter={onMouseEnterHandler}
                onMouseLeave={onMouseLeaveHandler}
              >
                <TreeNodeIcon>
                  {nodeStatus
                    ? <Icon color={STATUS_COLORS[nodeStatus]} />
                    : <Icon color={node.data.color ? node.data.color : theme.colors.colors.default} />}
                </TreeNodeIcon>
              </TreeNodeToolTip>
            )}
          />
        }
        {checkTree
            && (node.data.nodeType === OBJECT_ELEMENT || node.data.nodeType === INTEGRATION)
            && !!node.children.length &&
          <CheckBox
            id="childrenCheck"
            value={node.data.childrenChecked}
            onChange={handleCheckChildren}
            selectAll
            childSelect={!node.data.childrenChecked && node.children.some(child => child.data.checked) }
          />
        }
        <TreeNodeInfo>
          {returnName({name: node.data.name, type: node.data.treeNodeType, nodeType: node.data.nodeType})}
          <TreeNodeObjects dark={node.options.selected}>
          {renderChildrenCount()}
          </TreeNodeObjects>
        </TreeNodeInfo>
      </TreeNodeIconAndInfo>
      {hasChildrenTest() && !node.options.loading && (
        <TreeNodeArrow transform={node.options.opened} onClick={handleToggle}>
          <ArrowIcon />
        </TreeNodeArrow>
      )}

      {node.data.isLast && (
        <ConteinerButtonGetMore>
          {localLoading ?
          <Loader center />
            :
          <ButtonGetMore onClick={hendleGetMore}>
            {<Lang id={`menu.getMore`}/>}
          </ButtonGetMore >}
        </ConteinerButtonGetMore>
      )}
      {node.options.loading && (
        <Loader fill />
      )}
    </TreeNode>
  )
}

TreeNodeContainer.propTypes = {
  onToggle: pt.func,
  onSelect: pt.func,
  onSelectNode: pt.func,
  node: pt.shape({
    children: pt.arrayOf([pt.object]),
    data: pt.shape({
      name: pt.string,
      type: pt.string,
      status: pt.string,
      childrenCount: pt.number,
    }),
    options: pt.shape({
      selected: pt.bool,
      loading: pt.bool,
      opened: pt.bool,
      root: pt.bool,
    }),
  }),
}

TreeNodeContainer.defaultProps = {
  onToggle: noop,
  onSelect: noop,
  onSelectNode: noop,
  node: {},
}

export default injectIntl(TreeNodeContainer)
